import * as XLSX from 'xlsx';
import { fileSlug } from '@/lib/file-slug';
import { buildStoredZip } from '@/utils/storedZip';
import { CAPTURE_PHOTO_BLOCK_SIZE, speciesLabel } from './captureExportModel';
import type { CaptureExportRow, CapturePhoto } from './types';
import { formatCaptureClock, illuminationPercent } from './captureLocalTime';

export type PhotoFetcher = (captureId: string, mediaId: string | null) => Promise<Uint8Array>;

export interface PlannedPhoto {
    captureId: string;
    photo: CapturePhoto;
    name: string;
}

/** Instantánea de una exportación: datos y bloques de fotos salen de la misma lista. */
export interface PreparedCaptureExport {
    rows: CaptureExportRow[];
    /** Filtros tal como se enviaron a la RPC; se copian al JSON para dar contexto. */
    filters: Record<string, unknown>;
    includeAllPhotos: boolean;
    truncated: boolean;
    createdAt: Date;
}

export interface ZipFile {
    filename: string;
    bytes: Uint8Array;
}

export interface PhotoBlockZip extends ZipFile {
    photoCount: number;
    failures: string[];
}

const PRIVATE_NOTE =
    'Captura PRIVADA: su autor no la ha compartido. Solo para análisis interno; no usar su foto ni sus datos en piezas publicadas.';

function visibilityLabel(row: CaptureExportRow): 'publica' | 'privada' {
    return row.isPublic ? 'publica' : 'privada';
}

export function photoBlockCount(rows: readonly unknown[]): number {
    return Math.ceil(rows.length / CAPTURE_PHOTO_BLOCK_SIZE);
}

/** Bloque (desde 1) de la captura en la posición `index` de la lista exportada. */
export function photoBlockOf(index: number): number {
    return Math.floor(index / CAPTURE_PHOTO_BLOCK_SIZE) + 1;
}

export function photoBlockRows<T>(rows: readonly T[], block: number): T[] {
    const start = (block - 1) * CAPTURE_PHOTO_BLOCK_SIZE;
    return rows.slice(start, start + CAPTURE_PHOTO_BLOCK_SIZE);
}

export function planPhotos(rows: readonly CaptureExportRow[], includeAllPhotos: boolean): PlannedPhoto[] {
    const planned: PlannedPhoto[] = [];
    for (const row of rows) {
        const photos = includeAllPhotos ? row.photos : row.photos.slice(0, 1);
        const date = row.captureDate?.slice(0, 10) || 'sin-fecha';
        const base = `${date}_${fileSlug(speciesLabel(row), 'especie')}_${visibilityLabel(row)}_${row.id.slice(0, 8)}`;
        photos.forEach((photo, index) => {
            planned.push({ captureId: row.id, photo, name: `fotos/${base}_${index + 1}.jpg` });
        });
    }
    return planned;
}

function photoNamesByCapture(planned: readonly PlannedPhoto[]): Map<string, string[]> {
    const byCapture = new Map<string, string[]>();
    for (const item of planned) {
        const list = byCapture.get(item.captureId) ?? [];
        list.push(item.name);
        byCapture.set(item.captureId, list);
    }
    return byCapture;
}

function captureJson(row: CaptureExportRow, photoNames: string[], block: number) {
    const clock = formatCaptureClock(row.captureDate, row.latitude, row.longitude);
    return {
        id: row.id,
        fecha: row.captureDate,
        hora_utc: clock.horaUtc,
        hora_local: clock.horaLocal,
        zona_horaria: clock.zonaHoraria,
        visibilidad: visibilityLabel(row),
        ...(row.isPublic ? {} : { aviso_privacidad: PRIVATE_NOTE }),
        especie: {
            nombre_comun: row.speciesCommonName,
            nombre_cientifico: row.species,
            nombre_puesto_por_el_usuario: row.userCommonName,
            identificacion_dudosa: row.speciesLabelUnreliable,
        },
        peso_kg: row.weightKg,
        talla_cm: row.lengthCm,
        senuelo_o_cebo: row.baitLure,
        tipo_cebo: row.baitType,
        tecnica: row.technique,
        plataforma: row.fishingPlatform,
        tipo_agua: row.waterType,
        zona: {
            descripcion: row.location,
            latitud_aprox: row.latitude,
            longitud_aprox: row.longitude,
            distancia_al_centro_km: row.distanceKm,
        },
        condiciones: {
            fase_lunar: row.moonPhase,
            edad_lunar_dias: row.moonAgeDays,
            iluminacion_pct: illuminationPercent(row.moonIllumination),
            claridad_agua: row.waterClarity,
            marea: row.tideState,
            temperatura_agua: row.waterTemperature,
            temperatura_aire: row.airTemperature,
            viento: row.windSpeed,
            tiempo: row.weatherConditions,
        },
        descripcion_del_autor: row.description,
        autor: { usuario: row.authorUsername, nombre: row.authorDisplayName },
        interaccion: { likes: row.likesCount, comentarios: row.commentsCount },
        bloque_fotos: photoNames.length ? block : null,
        fotos: photoNames,
    };
}

const SHEET_HEADERS = [
    'id',
    'fecha',
    'visibilidad',
    'especie',
    'nombre_cientifico',
    'identificacion_dudosa',
    'peso_kg',
    'talla_cm',
    'senuelo_o_cebo',
    'tipo_cebo',
    'tecnica',
    'plataforma',
    'tipo_agua',
    'zona',
    'latitud_aprox',
    'longitud_aprox',
    'distancia_al_centro_km',
    'fase_lunar',
    'edad_lunar_dias',
    'iluminacion_pct',
    'claridad_agua',
    'hora_utc',
    'hora_local',
    'zona_horaria',
    'marea',
    'likes',
    'comentarios',
    'autor',
    'bloque_fotos',
    'fotos',
] as const;

type Cell = string | number | null;

function sheetRow(row: CaptureExportRow, photoNames: string[], block: number): Cell[] {
    const clock = formatCaptureClock(row.captureDate, row.latitude, row.longitude);
    return [
        row.id,
        row.captureDate?.slice(0, 10) ?? null,
        visibilityLabel(row),
        speciesLabel(row),
        row.species,
        row.speciesLabelUnreliable ? 'sí' : 'no',
        row.weightKg,
        row.lengthCm,
        row.baitLure,
        row.baitType,
        row.technique,
        row.fishingPlatform,
        row.waterType,
        row.location,
        row.latitude,
        row.longitude,
        row.distanceKm,
        row.moonPhase,
        row.moonAgeDays,
        illuminationPercent(row.moonIllumination),
        row.waterClarity,
        clock.horaUtc,
        clock.horaLocal,
        clock.zonaHoraria,
        row.tideState,
        row.likesCount,
        row.commentsCount,
        row.authorUsername,
        photoNames.length ? block : null,
        photoNames.join(' | '),
    ];
}

function workbookBytes(rows: Cell[][]): Uint8Array {
    const ws = XLSX.utils.aoa_to_sheet([[...SHEET_HEADERS], ...rows]);
    ws['!cols'] = SHEET_HEADERS.map((header) => ({ wch: Math.min(40, Math.max(12, header.length + 2)) }));
    ws['!autofilter'] = { ref: XLSX.utils.encode_range({ r: 0, c: 0 }, { r: rows.length, c: SHEET_HEADERS.length - 1 }) };
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Capturas');
    return new Uint8Array(XLSX.write(wb, { bookType: 'xlsx', type: 'array' }) as ArrayBuffer);
}

export function buildReadme(meta: {
    generatedAt: Date;
    total: number;
    privateCount: number;
    photoCount: number;
    blocks: number;
    truncated: boolean;
    filters: Record<string, unknown>;
}): string {
    return [
        '# Capturas AiPeces para creatividades',
        '',
        `Generado: ${meta.generatedAt.toISOString()}`,
        `Capturas: ${meta.total} (${meta.total - meta.privateCount} públicas, ${meta.privateCount} privadas)`,
        `Fotos: ${meta.photoCount}, repartidas en ${meta.blocks} ZIP de fotos (${CAPTURE_PHOTO_BLOCK_SIZE} capturas por bloque)`,
        ...(meta.truncated
            ? ['', '> El filtro tenía más capturas de las que caben en una exportación. Esta lista está cortada: acota los filtros si necesitas el resto.']
            : []),
        '',
        '## Archivos',
        '',
        '- `capturas.json`: una entrada por captura con todos los datos. Es el archivo de referencia.',
        '- `capturas.xlsx`: los mismos datos en tabla, para Excel o Sheets.',
        '- Los ZIP `…-fotos-NN-de-MM.zip` traen las fotos. Descomprímelos todos en la misma carpeta que este README: cada foto queda en la ruta `fotos/…` que indica el campo `fotos` del JSON.',
        '- `bloque_fotos` dice en qué ZIP de fotos están las de cada captura. Si una foto no se pudo descargar, su ZIP incluye `errores-bloque-N.txt`.',
        '- Las fotos son JPEG reducidos a 1600 px de lado como máximo. El nombre empieza por fecha, especie, visibilidad e id corto.',
        '',
        '## Privacidad (léelo antes de usar nada)',
        '',
        '- Cada captura lleva `visibilidad`: `publica` o `privada`. El nombre de cada foto también lo indica.',
        '- Las capturas **privadas** no las ha compartido su autor. Sirven para analizar tendencias (especies, señuelos, zonas, tallas), pero **ni su foto ni sus datos pueden aparecer en una pieza publicada**.',
        '- Las **públicas** están visibles en la comunidad de AiPeces. Antes de publicar una foto concreta en una campaña, pide permiso al autor o cítalo (`autor.usuario`).',
        '- Las coordenadas están redondeadas a 2 decimales (unos 1 km). No son el punto exacto de pesca.',
        '- No se incluyen borradores, capturas ocultadas por moderación ni notas privadas del usuario.',
        '',
        '## Campos',
        '',
        '- `peso_kg` y `talla_cm`: los declara el pescador; pueden faltar.',
        '- `especie.identificacion_dudosa`: la comunidad o la IA han marcado la especie como poco fiable.',
        '- `senuelo_o_cebo`: texto libre del pescador (p. ej. «Vinilo», «Minnow», «Navaja»). `tipo_cebo` es la categoría (`señuelo` o `cebo`).',
        '- `plataforma`: costa, kayak o embarcación. Vacío si el histórico no permite deducirlo.',
        '- `condiciones.fase_lunar`, `edad_lunar_dias` (0–29,53 desde la luna nueva) e `iluminacion_pct` (0–100). Los ocho tramos son iguales y están centrados en la fase.',
        '- `hora_utc` es la hora del instante guardado. `hora_local` y `zona_horaria` salen de las coordenadas (Madrid, Lisboa o Canarias; fuera de ahí, un huso fijo).',
        '- `tecnica` y `tipo_agua`: códigos internos de la App (`spinning`, `surfcasting`, `salada`, `dulce`…).',
        '- `zona.distancia_al_centro_km`: solo si se filtró por radio.',
        '- `interaccion.likes`: me gusta recibidos en el feed; sirve para elegir las fotos más atractivas.',
        '',
        '## Filtros aplicados',
        '',
        '```json',
        JSON.stringify(meta.filters, null, 2),
        '```',
        '',
    ].join('\n');
}

function stamp(date: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}-${pad(date.getHours())}${pad(date.getMinutes())}`;
}

export function exportBaseName(createdAt: Date): string {
    return `aipeces-capturas-${stamp(createdAt)}`;
}

/** README + JSON + Excel de todas las capturas, sin fotos. */
export function buildCaptureDataZip(prepared: PreparedCaptureExport): ZipFile {
    const { rows, createdAt } = prepared;
    const names = photoNamesByCapture(planPhotos(rows, prepared.includeAllPhotos));
    const privateCount = rows.filter((row) => !row.isPublic).length;
    const blocks = photoBlockCount(rows);
    let photoCount = 0;
    for (const list of names.values()) photoCount += list.length;

    const json = {
        generado_en: createdAt.toISOString(),
        total_capturas: rows.length,
        capturas_publicas: rows.length - privateCount,
        capturas_privadas: privateCount,
        lista_cortada: prepared.truncated,
        bloques_de_fotos: blocks,
        nota: 'Lee README.md. Las capturas con visibilidad "privada" no pueden aparecer en piezas publicadas.',
        filtros: prepared.filters,
        capturas: rows.map((row, index) => captureJson(row, names.get(row.id) ?? [], photoBlockOf(index))),
    };
    const readme = buildReadme({
        generatedAt: createdAt,
        total: rows.length,
        privateCount,
        photoCount,
        blocks,
        truncated: prepared.truncated,
        filters: prepared.filters,
    });

    const encoder = new TextEncoder();
    return {
        filename: `${exportBaseName(createdAt)}-datos.zip`,
        bytes: buildStoredZip(
            [
                { name: 'README.md', data: encoder.encode(readme) },
                { name: 'capturas.json', data: encoder.encode(JSON.stringify(json, null, 2)) },
                {
                    name: 'capturas.xlsx',
                    data: workbookBytes(rows.map((row, index) => sheetRow(row, names.get(row.id) ?? [], photoBlockOf(index)))),
                },
            ],
            createdAt,
        ),
    };
}

async function fetchAllPhotos(
    planned: readonly PlannedPhoto[],
    fetchPhoto: PhotoFetcher,
    concurrency: number,
    onProgress?: (done: number, total: number) => void,
): Promise<{ files: Map<string, Uint8Array>; failures: string[] }> {
    const files = new Map<string, Uint8Array>();
    const failures: string[] = [];
    let next = 0;
    let done = 0;

    async function worker() {
        while (next < planned.length) {
            const item = planned[next++]!;
            try {
                files.set(item.name, await fetchPhoto(item.captureId, item.photo.mediaId));
            } catch (error) {
                const message = error instanceof Error ? error.message : 'error desconocido';
                failures.push(`${item.name}: ${message}`);
            }
            done += 1;
            onProgress?.(done, planned.length);
        }
    }

    await Promise.all(Array.from({ length: Math.max(1, Math.min(concurrency, planned.length)) }, worker));
    return { files, failures };
}

/** Fotos de un bloque de la exportación, en `fotos/` para descomprimir junto al ZIP de datos. */
export async function buildCapturePhotoBlockZip(
    prepared: PreparedCaptureExport,
    block: number,
    options: {
        fetchPhoto: PhotoFetcher;
        onProgress?: (done: number, total: number) => void;
        concurrency?: number;
    },
): Promise<PhotoBlockZip> {
    const planned = planPhotos(photoBlockRows(prepared.rows, block), prepared.includeAllPhotos);
    const { files, failures } = await fetchAllPhotos(
        planned,
        options.fetchPhoto,
        options.concurrency ?? 4,
        options.onProgress,
    );
    const entries = planned
        .filter((item) => files.has(item.name))
        .map((item) => ({ name: item.name, data: files.get(item.name)! }));
    if (failures.length > 0) {
        entries.push({ name: `errores-bloque-${block}.txt`, data: new TextEncoder().encode(`${failures.join('\n')}\n`) });
    }
    const total = photoBlockCount(prepared.rows);
    const pad = (n: number) => String(n).padStart(String(total).length < 2 ? 2 : String(total).length, '0');
    return {
        filename: `${exportBaseName(prepared.createdAt)}-fotos-${pad(block)}-de-${pad(total)}.zip`,
        bytes: buildStoredZip(entries, prepared.createdAt),
        photoCount: files.size,
        failures,
    };
}
